/**
 * What a car accumulates: lifetime totals, fill-ups and the trip log.
 *
 * Keeps three things in step: what the session is counting right now, what is
 * stored for this car, and the fuel correction those fill-ups imply. The
 * simulator is walled off throughout -- it must never add a kilometre to a
 * real car's lifetime figures, the same way it never teaches it a gear ratio.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fuelCalibrationFrom, identityKey, sanitizeFill, tankSpans,
  type FuelCalibration, type FuelFill, type JourneyScope, type ObdSession, type TankSpan,
  type TripRecord, type VehicleIdentity,
} from '@obd/core';
import {
  UNKNOWN_CAR, adoptRecords, clearTrips, deleteFill, loadCar, resolveCar, saveFill, saveTotals, saveTrip,
  storageReport, requestPersistentStorage, type StorageReport, type StoredTrip,
} from './db';

export interface FillInput {
  litres: number;
  /** Filled right up: only these give a true consumption figure. */
  full: boolean;
  pricePerL?: number;
  odometerKm?: number;
  note?: string;
  /** Epoch ms; now if left out. */
  t?: number;
}

export interface CarRecordsApi {
  /** Which car these records belong to. */
  key: string;
  /** False until the stored records have been read. Nothing is saved before. */
  ready: boolean;
  fills: FuelFill[];
  trips: StoredTrip[];
  /** Full-to-full spans, newest last. */
  spans: TankSpan[];
  /** What the fill-ups say about the fuel estimate, if enough of them agree. */
  calibration: FuelCalibration | undefined;
  storage: StorageReport;
  /** True while the simulator is running, when nothing may be recorded. */
  locked: boolean;
  addFill: (input: FillInput) => Promise<FuelFill | undefined>;
  removeFill: (id: string) => Promise<void>;
  /** Start a span over. 'tank' also happens by itself when a fill-up is added. */
  resetScope: (scope: JourneyScope) => Promise<void>;
  clearTripLog: () => Promise<void>;
  askForPersistence: () => Promise<void>;
}

/** How often the running totals are written out while driving. */
const SAVE_EVERY_MS = 15_000;

export function useCarRecords({
  session, car, simulating, lastTrip, onCalibrationChange,
}: {
  session: ObdSession;
  /** The car in use: the one connected now, or the one last connected. */
  car: VehicleIdentity | undefined;
  simulating: boolean;
  /** The drive that just ended, from the session provider. */
  lastTrip: TripRecord | null;
  /** Called when the fuel correction changes, so it can be saved with the rest. */
  onCalibrationChange?: () => void;
}): CarRecordsApi {
  const [key, setKey] = useState(UNKNOWN_CAR);
  const [ready, setReady] = useState(false);
  const [fills, setFills] = useState<FuelFill[]>([]);
  const [trips, setTrips] = useState<StoredTrip[]>([]);
  const [storage, setStorage] = useState<StorageReport>({ persisted: false, durable: true });

  const simulatingRef = useRef(simulating);
  simulatingRef.current = simulating;
  const keyRef = useRef(key);
  keyRef.current = key;
  const readyRef = useRef(false);
  readyRef.current = ready;
  const calibrationRef = useRef<FuelCalibration | undefined>(undefined);

  const carId = identityKey(car);

  const applyCalibration = useCallback(
    (list: FuelFill[]) => {
      const cal = fuelCalibrationFrom(list);
      calibrationRef.current = cal;
      if (!simulatingRef.current) {
        session.setFuelCalibration(cal);
        onCalibrationChange?.();
      }
    },
    [session, onCalibrationChange],
  );

  // ------------------------------------------------------------- loading
  //
  // Records follow the car. Anything recorded before the car was identified is
  // adopted by the first car that is, so the first drive is not thrown away.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    void (async () => {
      const resolved = await resolveCar(car);
      if (cancelled) return;
      if (resolved !== UNKNOWN_CAR) await adoptRecords(UNKNOWN_CAR, resolved);
      const records = await loadCar(resolved);
      if (cancelled) return;

      setKey(resolved);
      keyRef.current = resolved;
      setFills(records.fills);
      setTrips(records.trips);
      applyCalibration(records.fills);
      // The simulator gets a blank slate; the real totals wait for it to stop.
      session.importJourney(simulatingRef.current ? undefined : records.totals);
      setReady(true);
      readyRef.current = true;
      void storageReport().then((report) => {
        if (!cancelled) setStorage(report);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [carId, session, applyCalibration, car]);

  // The simulator borrows the session: swap the real totals out and back.
  useEffect(() => {
    if (!readyRef.current) return;
    let cancelled = false;
    void (async () => {
      if (simulating) {
        await saveTotals(keyRef.current, session.exportJourney());
        session.importJourney(undefined);
        session.setFuelCalibration(undefined);
      } else {
        const records = await loadCar(keyRef.current);
        if (cancelled) return;
        session.importJourney(records.totals);
        session.setFuelCalibration(calibrationRef.current);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [simulating, session]);

  // ------------------------------------------------------------- saving
  const save = useCallback(() => {
    if (simulatingRef.current || !readyRef.current) return;
    void saveTotals(keyRef.current, session.exportJourney());
  }, [session]);

  useEffect(() => {
    const id = setInterval(save, SAVE_EVERY_MS);
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') save();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
      save();
    };
  }, [save]);

  // A finished drive goes into the log.
  useEffect(() => {
    if (!lastTrip || simulatingRef.current || !readyRef.current) return;
    if (lastTrip.distanceKm < 0.1) return;
    const stored: StoredTrip = {
      ...lastTrip,
      id: keyRef.current + ':' + lastTrip.endedAt + ':' + Math.round(lastTrip.distanceKm * 1000),
      carKey: keyRef.current,
    };
    setTrips((prev) => (prev.some((t) => t.id === stored.id) ? prev : [stored, ...prev]));
    void saveTrip(stored).then(save);
  }, [lastTrip, save]);

  // ------------------------------------------------------------ fill-ups
  const addFill = useCallback(
    async (input: FillInput): Promise<FuelFill | undefined> => {
      if (simulatingRef.current) return undefined;
      // The tank span is exactly the distance and estimated fuel since the
      // last fill-up, which is what makes the pump figure comparable.
      const tank = session.summary('tank');
      const fill = sanitizeFill({
        id: 'f' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        t: input.t ?? Date.now(),
        litres: input.litres,
        full: input.full,
        pricePerL: input.pricePerL,
        odometerKm: input.odometerKm,
        note: input.note,
        distanceKm: tank.distanceKm,
        estimatedL: tank.fuelL,
      });
      if (!fill) return undefined;

      const next = [...fills, fill].sort((a, b) => a.t - b.t);
      setFills(next);
      applyCalibration(next);
      session.resetScope('tank');
      await saveFill(keyRef.current, fill);
      save();
      return fill;
    },
    [fills, session, applyCalibration, save],
  );

  const removeFill = useCallback(
    async (id: string) => {
      const next = fills.filter((f) => f.id !== id);
      setFills(next);
      applyCalibration(next);
      await deleteFill(keyRef.current, id);
    },
    [fills, applyCalibration],
  );

  const resetScope = useCallback(
    async (scope: JourneyScope) => {
      session.resetScope(scope);
      if (!simulatingRef.current) await saveTotals(keyRef.current, session.exportJourney());
    },
    [session],
  );

  const clearTripLog = useCallback(async () => {
    setTrips([]);
    await clearTrips(keyRef.current);
  }, []);

  const askForPersistence = useCallback(async () => {
    setStorage(await requestPersistentStorage());
  }, []);

  const spans = useMemo(() => tankSpans(fills), [fills]);

  return useMemo(
    () => ({
      key,
      ready,
      fills,
      trips,
      spans,
      calibration: calibrationRef.current,
      storage,
      locked: simulating,
      addFill,
      removeFill,
      resetScope,
      clearTripLog,
      askForPersistence,
    }),
    [key, ready, fills, trips, spans, storage, simulating, addFill, removeFill, resetScope, clearTripLog, askForPersistence],
  );
}
